import {
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Cog,
  Maximize2,
  Minus,
  Plus,
  User,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, WheelEvent } from 'react';

import type { OrgTreeNode, UnitType } from '../../hooks/useOrganizationalStructure';

import { UNIT_CONFIG } from './konfiguracja';
import {
  KROK_SKALI,
  SKALA_MAX,
  SKALA_MIN,
  domyslnieZwiniete,
  ograniczSkale,
  policzTypy,
  skalaDopasowania,
  ukladDzieci,
  wszystkieZDziecmi,
} from './uklad';

// =============================================================================
// Diagram struktury organizacyjnej — układ hybrydowy.
//
// Działy z poddziałami idą w poziomy wiersz (klasyczny nawias schematu),
// stanowiska pod działem — w pionową kolumnę. Dzięki temu szerokość zależy od
// głębokości drzewa, a nie od liczby stanowisk, i dziesięć stanowisk pod
// „Produkcją” mieści się na telefonie. Reguły układu, skali i zwijania leżą
// w `uklad.ts`; tutaj jest tylko rysowanie i okno z narzędziami.
// Uzasadnienie i pomiary: docs/07-DESIGN/DIAGRAM_STRUKTURY_ORGANIZACYJNEJ.md
// =============================================================================

const KOLOR_ORGANIZACJI = '#F59E0B';
const PROG_PRZECIAGNIECIA_PX = 4;
/** Odstęp treści od krawędzi okna — żeby po dopasowaniu karty nie kleiły się do ramki. */
const MARGINES_TRESCI_PX = 32;

interface Sterowanie {
  zwiniete: Set<string>;
  przelacz: (id: string) => void;
}

// ─── Wspólne drobiazgi karty ─────────────────────────────────────────────────

function Liczniki({ node, className = '' }: { node: OrgTreeNode; className?: string }) {
  const pracownicy = node.employee_count ?? 0;
  const sprzet = node.equipment_count ?? 0;
  if (pracownicy === 0 && sprzet === 0) return null;
  return (
    <span className={`flex items-center gap-2 text-[11px] text-aurora-muted mono ${className}`}>
      {pracownicy > 0 && (
        <span className="flex items-center gap-0.5" title="Pracownicy">
          <User className="w-3 h-3" />
          {pracownicy}
        </span>
      )}
      {sprzet > 0 && (
        <span className="flex items-center gap-0.5" title="Sprzęt">
          <Cog className="w-3 h-3" />
          {sprzet}
        </span>
      )}
    </span>
  );
}

function PlakietkaTypu({ typ, kolor }: { typ: UnitType; kolor: string }) {
  return (
    <span
      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: `${kolor}1F`, color: kolor }}
    >
      {UNIT_CONFIG[typ].labelPl}
    </span>
  );
}

// ─── Karta w wierszu (pionowa) ───────────────────────────────────────────────

function KartaWiersz({ node, sterowanie }: { node: OrgTreeNode; sterowanie: Sterowanie }) {
  const config = UNIT_CONFIG[node.unit_type];
  const Icon = config.icon;
  const kolor = config.defaultColor;
  const maDzieci = node.children.length > 0;
  const rozwinieta = maDzieci && !sterowanie.zwiniete.has(node.id);

  const tresc = (
    <>
      <div
        className="w-10 h-10 rounded-ids-lg flex items-center justify-center"
        style={{ backgroundColor: `${kolor}26` }}
      >
        <Icon className="w-5 h-5" style={{ color: kolor }} />
      </div>
      <span className="text-sm font-medium text-aurora-text text-center leading-tight line-clamp-2 break-words">
        {node.name}
      </span>
      <PlakietkaTypu typ={node.unit_type} kolor={kolor} />
      <Liczniki node={node} />
    </>
  );

  const klasa =
    'relative w-[168px] px-3 py-3 rounded-ids-lg border-2 transition-[border-color,box-shadow] duration-200 ' +
    'flex flex-col items-center gap-1.5 text-left hover:shadow-elev-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora-brand';
  const styl: CSSProperties = { backgroundColor: `${kolor}14`, borderColor: `${kolor}55` };

  return (
    <div className="flex flex-col items-center" data-wezel={node.id}>
      {maDzieci ? (
        <button
          type="button"
          aria-expanded={rozwinieta}
          onClick={() => sterowanie.przelacz(node.id)}
          className={`${klasa} cursor-pointer`}
          style={styl}
        >
          {tresc}
          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 min-w-6 h-6 px-1.5 rounded-full bg-aurora-raised border border-aurora-border-strong flex items-center justify-center gap-0.5 z-10 text-[10px] text-aurora-muted font-medium mono">
            {rozwinieta ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <>
                <ChevronRight className="w-3 h-3" />
                {node.children.length}
              </>
            )}
          </span>
        </button>
      ) : (
        <div className={klasa} style={styl}>
          {tresc}
        </div>
      )}

      {rozwinieta && (
        <>
          <div className="w-0.5 h-6 bg-aurora-border-strong" />
          <Dzieci node={node} sterowanie={sterowanie} />
        </>
      )}
    </div>
  );
}

// ─── Karta w kolumnie (pozioma, kompaktowa) ──────────────────────────────────

function KartaKolumna({ node, sterowanie }: { node: OrgTreeNode; sterowanie: Sterowanie }) {
  const config = UNIT_CONFIG[node.unit_type];
  const Icon = config.icon;
  const kolor = config.defaultColor;
  const maDzieci = node.children.length > 0;
  const rozwinieta = maDzieci && !sterowanie.zwiniete.has(node.id);

  const klasa =
    'w-[240px] flex items-center gap-2.5 px-3 py-2 rounded-ids-md border transition-[border-color,box-shadow] duration-200 ' +
    'text-left hover:shadow-elev-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora-brand';
  const styl: CSSProperties = {
    backgroundColor: `${kolor}14`,
    borderColor: `${kolor}55`,
    borderLeftWidth: 3,
    borderLeftColor: kolor,
  };

  const tresc = (
    <>
      <div
        className="w-7 h-7 rounded-ids-sm flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${kolor}26` }}
      >
        <Icon className="w-3.5 h-3.5" style={{ color: kolor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-aurora-text leading-tight truncate">
          {node.name}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className="text-[9px] uppercase tracking-wider font-semibold"
            style={{ color: kolor }}
          >
            {config.labelPl}
          </span>
          <Liczniki node={node} />
        </div>
      </div>
      {maDzieci && (
        <span className="flex items-center gap-0.5 text-[10px] text-aurora-muted mono flex-shrink-0">
          {node.children.length}
          {rozwinieta ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </span>
      )}
    </>
  );

  return (
    <li className="relative pl-5" data-wezel={node.id}>
      {/* Poprzeczka od kręgosłupa do karty */}
      <span className="absolute left-0 top-[22px] w-5 h-0.5 bg-aurora-border-strong" aria-hidden />
      {maDzieci ? (
        <button
          type="button"
          aria-expanded={rozwinieta}
          onClick={() => sterowanie.przelacz(node.id)}
          className={`${klasa} cursor-pointer`}
          style={styl}
        >
          {tresc}
        </button>
      ) : (
        <div className={klasa} style={styl}>
          {tresc}
        </div>
      )}
      {rozwinieta && (
        <div className="mt-2">
          <Kolumna node={node} sterowanie={sterowanie} />
        </div>
      )}
    </li>
  );
}

/** Pionowa lista dzieci z kręgosłupem po lewej; poddrzewa zagnieżdżają się w niej wcięte. */
function Kolumna({ node, sterowanie }: { node: OrgTreeNode; sterowanie: Sterowanie }) {
  return (
    <ul
      className="relative flex flex-col gap-2 pl-3 before:absolute before:left-3 before:top-0 before:bottom-[22px] before:w-0.5 before:bg-aurora-border-strong"
      data-uklad="kolumna"
    >
      {node.children.map((dziecko) => (
        <KartaKolumna key={dziecko.id} node={dziecko} sterowanie={sterowanie} />
      ))}
    </ul>
  );
}

/** Poziomy wiersz rodzeństwa spięty klasycznym nawiasem schematu. */
function Wiersz({ nodes, sterowanie }: { nodes: OrgTreeNode[]; sterowanie: Sterowanie }) {
  const wiele = nodes.length > 1;
  return (
    <div className="flex items-start" data-uklad="wiersz">
      {nodes.map((dziecko, idx) => (
        <div key={dziecko.id} className="relative flex flex-col items-center px-2 pt-6">
          {wiele && (
            <span
              className="absolute top-0 h-0.5 bg-aurora-border-strong"
              style={{
                left: idx === 0 ? '50%' : 0,
                right: idx === nodes.length - 1 ? '50%' : 0,
              }}
              aria-hidden
            />
          )}
          <span
            className="absolute top-0 left-1/2 w-0.5 h-6 -translate-x-1/2 bg-aurora-border-strong"
            aria-hidden
          />
          <KartaWiersz node={dziecko} sterowanie={sterowanie} />
        </div>
      ))}
    </div>
  );
}

/** Dzieci węzła w układzie wybranym regułą z `uklad.ts`. */
function Dzieci({ node, sterowanie }: { node: OrgTreeNode; sterowanie: Sterowanie }) {
  if (ukladDzieci(node) === 'kolumna') {
    // Kolumna wisi na łączniku rodzica: kręgosłup (12 px od lewej krawędzi `ul`)
    // ma stanąć dokładnie pod środkiem karty. Element ma 264 px, więc lewy
    // margines 240 px przesuwa go tak, że środek osi rodzica wypada na kręgosłupie.
    return (
      <div className="w-[264px] ml-[240px]">
        <Kolumna node={node} sterowanie={sterowanie} />
      </div>
    );
  }
  return <Wiersz nodes={node.children} sterowanie={sterowanie} />;
}

// ─── Okno podglądu: skala, przesuwanie, dopasowanie ──────────────────────────

interface OknoProps {
  children: React.ReactNode;
  skala: number;
  onSkala: (skala: number) => void;
  /** Rośnie przy każdym żądaniu „dopasuj” — okno mierzy treść i ustawia skalę. */
  zadanieDopasowania: number;
}

function OknoDiagramu({ children, skala, onSkala, zadanieDopasowania }: OknoProps) {
  const oknoRef = useRef<HTMLDivElement>(null);
  const trescRef = useRef<HTMLDivElement>(null);
  const [naturalny, setNaturalny] = useState({ w: 0, h: 0 });
  const przeciaganie = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  const przeciagnieto = useRef(false);

  // Rozmiar naturalny (bez skali) — potrzebny, bo `transform` nie powiększa obszaru przewijania.
  useLayoutEffect(() => {
    const el = trescRef.current;
    if (!el) return;
    const zmierz = () => setNaturalny({ w: el.offsetWidth, h: el.offsetHeight });
    zmierz();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(zmierz);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Numer ostatniego wykonanego żądania. Efekt zależy też od pomiaru, bo przy
  // pierwszym renderze `naturalny` jest jeszcze zerem i dopasowanie musi
  // zaczekać na ResizeObserver — ale każde żądanie wykonuje się dokładnie raz,
  // więc zwinięcie węzła (nowy pomiar) nie zmienia skali za plecami użytkownika.
  const wykonane = useRef(0);
  useEffect(() => {
    if (zadanieDopasowania === wykonane.current) return;
    const okno = oknoRef.current;
    if (!okno || naturalny.w === 0) return;
    wykonane.current = zadanieDopasowania;
    const skalaNowa = skalaDopasowania(naturalny.w + MARGINES_TRESCI_PX * 2, okno.clientWidth);
    onSkala(skalaNowa);
    // Gdy treść i tak nie mieści się (dół zakresu skali), okno ma pokazać KORZEŃ,
    // czyli środek treści — a nie jej lewy skraj, jak przy `scrollLeft = 0`.
    const szerokoscPoSkali = naturalny.w * skalaNowa + MARGINES_TRESCI_PX * 2;
    okno.scrollTo({ left: Math.max(0, (szerokoscPoSkali - okno.clientWidth) / 2), top: 0 });
  }, [zadanieDopasowania, naturalny.w, onSkala]);

  const naPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const okno = oknoRef.current;
    if (!okno) return;
    przeciaganie.current = { x: e.clientX, y: e.clientY, sl: okno.scrollLeft, st: okno.scrollTop };
    przeciagnieto.current = false;
  };
  const naPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const start = przeciaganie.current;
    const okno = oknoRef.current;
    if (!start || !okno) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) > PROG_PRZECIAGNIECIA_PX || Math.abs(dy) > PROG_PRZECIAGNIECIA_PX) {
      przeciagnieto.current = true;
    }
    okno.scrollLeft = start.sl - dx;
    okno.scrollTop = start.st - dy;
  };
  const koniecPrzeciagania = () => {
    przeciaganie.current = null;
  };
  // Złapanie tła i pociągnięcie nie ma zwijać węzła, na którym puściliśmy mysz.
  const naClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!przeciagnieto.current) return;
    e.stopPropagation();
    e.preventDefault();
    przeciagnieto.current = false;
  };
  const naWheel = (e: WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    onSkala(ograniczSkale(e.deltaY < 0 ? skala * KROK_SKALI : skala / KROK_SKALI));
  };

  return (
    <div
      ref={oknoRef}
      data-testid="okno-diagramu"
      className="relative overflow-auto bg-aurora-bg-subtle/40 cursor-grab active:cursor-grabbing select-none"
      style={{
        height: 'clamp(420px, 65vh, 760px)',
        backgroundImage:
          'radial-gradient(circle, color-mix(in srgb, var(--ids-border) 70%, transparent) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}
      onPointerDown={naPointerDown}
      onPointerMove={naPointerMove}
      onPointerUp={koniecPrzeciagania}
      onPointerLeave={koniecPrzeciagania}
      onPointerCancel={koniecPrzeciagania}
      onClickCapture={naClickCapture}
      onWheel={naWheel}
    >
      {/* Pojemnik o jawnym rozmiarze `naturalny × skala`, bo transform go nie zmienia.
          Centruje się automarginesami, NIE flexem: blok węższy od okna staje na
          środku, blok szerszy zaczyna się od lewej krawędzi i da się przewinąć do
          korzenia. Flex `justify-center` przy treści szerszej niż okno wypychał
          korzeń w prawo poza ekran — dokładnie usterka ze zgłoszenia. */}
      <div
        style={{
          width: naturalny.w ? naturalny.w * skala + MARGINES_TRESCI_PX * 2 : undefined,
          height: naturalny.h ? naturalny.h * skala + MARGINES_TRESCI_PX * 2 : undefined,
          padding: MARGINES_TRESCI_PX,
          marginInline: 'auto',
        }}
      >
        <div
          ref={trescRef}
          className="flex flex-col items-center w-max"
          style={{ transform: `scale(${skala})`, transformOrigin: 'top left' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Komponent główny ────────────────────────────────────────────────────────

export interface OrgChartDiagramProps {
  tree: OrgTreeNode[];
  organizationName?: string;
  editable?: boolean;
  onAddUnit?: (parentId: string | null, type: UnitType) => void;
}

export function OrgChartDiagram({
  tree,
  organizationName,
  editable = false,
  onAddUnit,
}: OrgChartDiagramProps) {
  const [zwiniete, setZwiniete] = useState<Set<string>>(() => domyslnieZwiniete(tree));
  const [skala, setSkala] = useState(1);
  // Pierwszy render = jedno żądanie dopasowania; kolejne dopiero po kliknięciu „Dopasuj”.
  const [zadanieDopasowania, setZadanieDopasowania] = useState(1);

  const przelacz = useCallback((id: string) => {
    setZwiniete((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const sterowanie = useMemo<Sterowanie>(() => ({ zwiniete, przelacz }), [zwiniete, przelacz]);
  const ustawSkale = useCallback((s: number) => setSkala(ograniczSkale(s)), []);

  const liczby = useMemo(() => policzTypy(tree), [tree]);
  const podsumowanie = (['department', 'position', 'team'] as UnitType[])
    .filter((typ) => liczby[typ] > 0)
    .map((typ) => `${liczby[typ]} ${UNIT_CONFIG[typ].liczbaMnoga(liczby[typ])}`)
    .join(' · ');

  if (tree.length === 0) {
    // Diagram jest widokiem DOMYŚLNYM strony profilu, więc pusty stan musi dawać
    // to samo wyjście, co lista — inaczej administrator bez struktury ląduje
    // w ślepym zaułku i musi odkryć przełącznik „Lista”, żeby cokolwiek dodać.
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Building2 className="w-12 h-12 text-aurora-subtle mb-3" />
        <p className="text-aurora-muted">Brak struktury organizacyjnej</p>
        {editable && onAddUnit && (
          <button onClick={() => onAddUnit(null, 'department')} className="mt-4 btn btn--tonal">
            <Plus className="w-4 h-4" />
            Dodaj pierwszy dział
          </button>
        )}
      </div>
    );
  }

  const korzenJakoWezel: OrgTreeNode | null = organizationName
    ? ({
        id: '__organizacja__',
        name: organizationName,
        unit_type: 'department',
        parent_id: null,
        children: tree,
      } as unknown as OrgTreeNode)
    : null;

  return (
    <div className="rounded-ids-lg border border-aurora-border overflow-hidden">
      {/* Pasek narzędzi */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-aurora-border bg-aurora-surface">
        <span className="text-xs text-aurora-muted mono" data-testid="podsumowanie-struktury">
          {podsumowanie}
        </span>
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setZwiniete(new Set())}
            title="Rozwiń wszystkie węzły"
          >
            <ChevronsUpDown className="w-3.5 h-3.5" />
            Rozwiń
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setZwiniete(wszystkieZDziecmi(tree))}
            title="Zwiń wszystkie węzły"
          >
            <ChevronsDownUp className="w-3.5 h-3.5" />
            Zwiń
          </button>
          <span className="w-px h-5 bg-aurora-border mx-1" aria-hidden />
          <div className="segmented" role="group" aria-label="Przybliżenie">
            <button
              type="button"
              onClick={() => ustawSkale(skala / KROK_SKALI)}
              disabled={skala <= SKALA_MIN}
              aria-label="Pomniejsz"
              title="Pomniejsz"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => ustawSkale(1)}
              className="mono min-w-[52px]"
              title="Wróć do 100 %"
              data-testid="skala-procent"
            >
              {Math.round(skala * 100)}%
            </button>
            <button
              type="button"
              onClick={() => ustawSkale(skala * KROK_SKALI)}
              disabled={skala >= SKALA_MAX}
              aria-label="Powiększ"
              title="Powiększ"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setZadanieDopasowania((n) => n + 1)}
            title="Dopasuj do szerokości okna"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            Dopasuj
          </button>
          {editable && onAddUnit && (
            <>
              <span className="w-px h-5 bg-aurora-border mx-1" aria-hidden />
              <button
                type="button"
                onClick={() => onAddUnit(null, 'department')}
                className="btn btn--tonal btn--sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Dział najwyższego poziomu
              </button>
            </>
          )}
        </div>
      </div>

      <OknoDiagramu skala={skala} onSkala={ustawSkale} zadanieDopasowania={zadanieDopasowania}>
        {korzenJakoWezel ? (
          <>
            <div
              className="w-[200px] px-4 py-4 rounded-ids-xl border-2 flex flex-col items-center gap-2 shadow-elev-1"
              style={{
                borderColor: `${KOLOR_ORGANIZACJI}66`,
                backgroundColor: `${KOLOR_ORGANIZACJI}1A`,
              }}
              data-wezel="__organizacja__"
            >
              <div
                className="w-12 h-12 rounded-ids-md flex items-center justify-center"
                style={{ backgroundColor: `${KOLOR_ORGANIZACJI}33` }}
              >
                <Building2 className="w-6 h-6" style={{ color: KOLOR_ORGANIZACJI }} />
              </div>
              <span className="text-base font-semibold font-display text-aurora-text text-center leading-tight">
                {organizationName}
              </span>
              <span
                className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${KOLOR_ORGANIZACJI}26`,
                  color: KOLOR_ORGANIZACJI,
                }}
              >
                Organizacja
              </span>
            </div>
            <div className="w-0.5 h-8 bg-aurora-border-strong" />
            <Dzieci node={korzenJakoWezel} sterowanie={sterowanie} />
          </>
        ) : (
          <Wiersz nodes={tree} sterowanie={sterowanie} />
        )}
      </OknoDiagramu>
    </div>
  );
}
